import { useEffect, useRef } from 'react'

/**
 * EncryptionField - Security visualization for lukhas.id
 *
 * Features:
 * - Central hexagonal cipher with rotating rings
 * - Floating encryption keys/symbols
 * - Data streams flowing inward (encryption)
 * - Pulse waves emanating from center (protection)
 * - Monochrome white/black aesthetic
 */

interface DataStream {
  angle: number
  speed: number
  length: number
  offset: number
}

interface FloatingKey {
  x: number
  y: number
  char: string
  size: number
  opacity: number
  drift: number
  phase: number
}

export default function EncryptionField() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Fixed size for the visualization
    const size = 400
    canvas.width = size
    canvas.height = size

    const centerX = size / 2
    const centerY = size / 2

    // Encryption characters
    const cryptoChars = ['0', '1', 'Λ', '◊', '∴', '⬡', '◈', '⟐']

    // Create data streams (converging to center)
    const streams: DataStream[] = []
    for (let i = 0; i < 12; i++) {
      streams.push({
        angle: (i * Math.PI * 2) / 12,
        speed: 0.3 + Math.random() * 0.3,
        length: 30 + Math.random() * 40,
        offset: Math.random() * 100
      })
    }

    // Create floating keys
    const keys: FloatingKey[] = []
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8 + Math.random() * 0.5
      const dist = 100 + Math.random() * 60
      keys.push({
        x: centerX + Math.cos(angle) * dist,
        y: centerY + Math.sin(angle) * dist,
        char: cryptoChars[Math.floor(Math.random() * cryptoChars.length)],
        size: 10 + Math.random() * 6,
        opacity: 0.15 + Math.random() * 0.15,
        drift: Math.random() * Math.PI * 2,
        phase: Math.random() * Math.PI * 2
      })
    }

    let animationFrameId: number
    const startTime = Date.now()

    const drawHexagon = (x: number, y: number, radius: number, rotation: number = 0) => {
      ctx.beginPath()
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3 + rotation
        const px = x + Math.cos(angle) * radius
        const py = y + Math.sin(angle) * radius
        if (i === 0) {
          ctx.moveTo(px, py)
        } else {
          ctx.lineTo(px, py)
        }
      }
      ctx.closePath()
    }

    const animate = () => {
      const elapsed = (Date.now() - startTime) / 1000

      // Clear canvas
      ctx.clearRect(0, 0, size, size)

      // === OUTER PULSE RINGS ===
      for (let ring = 0; ring < 3; ring++) {
        const pulsePhase = (elapsed * 0.5 + ring * 0.3) % 1
        const pulseRadius = 40 + pulsePhase * 140
        const pulseOpacity = (1 - pulsePhase) * 0.15

        ctx.strokeStyle = `rgba(255, 255, 255, ${pulseOpacity})`
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2)
        ctx.stroke()
      }

      // === DATA STREAMS (converging) ===
      streams.forEach((stream, i) => {
        const streamProgress = ((elapsed * stream.speed + stream.offset) % 1)
        const startDist = 180 - streamProgress * 140
        const endDist = startDist - stream.length

        if (endDist > 40) {
          const startX = centerX + Math.cos(stream.angle) * startDist
          const startY = centerY + Math.sin(stream.angle) * startDist
          const endX = centerX + Math.cos(stream.angle) * Math.max(endDist, 40)
          const endY = centerY + Math.sin(stream.angle) * Math.max(endDist, 40)

          // Gradient line
          const gradient = ctx.createLinearGradient(startX, startY, endX, endY)
          gradient.addColorStop(0, 'rgba(255, 255, 255, 0)')
          gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.3)')
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0.1)')

          ctx.strokeStyle = gradient
          ctx.lineWidth = 1.5
          ctx.beginPath()
          ctx.moveTo(startX, startY)
          ctx.lineTo(endX, endY)
          ctx.stroke()

          // Leading dot
          ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
          ctx.beginPath()
          ctx.arc(endX, endY, 2, 0, Math.PI * 2)
          ctx.fill()
        }
      })

      // === FLOATING ENCRYPTION KEYS ===
      keys.forEach((key, i) => {
        const drift = Math.sin(elapsed * 0.5 + key.drift) * 8
        const vertDrift = Math.cos(elapsed * 0.3 + key.phase) * 5
        const opacity = key.opacity * (0.7 + Math.sin(elapsed * 0.8 + key.phase) * 0.3)

        ctx.font = `${key.size}px monospace`
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(key.char, key.x + drift, key.y + vertDrift)
      })

      // === HEXAGONAL CIPHER RINGS ===

      // Outer hex ring (slow rotation)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
      ctx.lineWidth = 1
      drawHexagon(centerX, centerY, 85, elapsed * 0.1)
      ctx.stroke()

      // Middle hex ring (opposite rotation)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)'
      ctx.lineWidth = 1.5
      drawHexagon(centerX, centerY, 60, -elapsed * 0.15)
      ctx.stroke()

      // Inner hex ring (faster)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
      ctx.lineWidth = 1
      drawHexagon(centerX, centerY, 38, elapsed * 0.25)
      ctx.stroke()

      // === CENTRAL CORE ===

      // Glow
      const glowPulse = 0.7 + Math.sin(elapsed * 1.5) * 0.3
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, 30
      )
      gradient.addColorStop(0, `rgba(255, 255, 255, ${0.4 * glowPulse})`)
      gradient.addColorStop(0.5, `rgba(255, 255, 255, ${0.15 * glowPulse})`)
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')

      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(centerX, centerY, 30, 0, Math.PI * 2)
      ctx.fill()

      // Inner core circle
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.4 * glowPulse})`
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(centerX, centerY, 18, 0, Math.PI * 2)
      ctx.stroke()

      // Core dot
      ctx.fillStyle = `rgba(255, 255, 255, ${0.8 * glowPulse})`
      ctx.beginPath()
      ctx.arc(centerX, centerY, 5, 0, Math.PI * 2)
      ctx.fill()

      // === SCANNER LINE ===
      const scanAngle = elapsed * 0.4
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(centerX, centerY)
      ctx.lineTo(
        centerX + Math.cos(scanAngle) * 180,
        centerY + Math.sin(scanAngle) * 180
      )
      ctx.stroke()

      // === CORNER BRACKETS (encryption frame) ===
      const bracketSize = 20
      const bracketOffset = 15
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)'
      ctx.lineWidth = 1

      // Top-left
      ctx.beginPath()
      ctx.moveTo(bracketOffset, bracketOffset + bracketSize)
      ctx.lineTo(bracketOffset, bracketOffset)
      ctx.lineTo(bracketOffset + bracketSize, bracketOffset)
      ctx.stroke()

      // Top-right
      ctx.beginPath()
      ctx.moveTo(size - bracketOffset - bracketSize, bracketOffset)
      ctx.lineTo(size - bracketOffset, bracketOffset)
      ctx.lineTo(size - bracketOffset, bracketOffset + bracketSize)
      ctx.stroke()

      // Bottom-left
      ctx.beginPath()
      ctx.moveTo(bracketOffset, size - bracketOffset - bracketSize)
      ctx.lineTo(bracketOffset, size - bracketOffset)
      ctx.lineTo(bracketOffset + bracketSize, size - bracketOffset)
      ctx.stroke()

      // Bottom-right
      ctx.beginPath()
      ctx.moveTo(size - bracketOffset - bracketSize, size - bracketOffset)
      ctx.lineTo(size - bracketOffset, size - bracketOffset)
      ctx.lineTo(size - bracketOffset, size - bracketOffset - bracketSize)
      ctx.stroke()

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
      style={{ opacity: 0.9 }}
      aria-hidden="true"
    />
  )
}
